import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { submitRehomingApplication } from '../services/api';

const RehomingApplicationPage = () => {
  const [formData, setFormData] = useState({
    applicantName: '',
    email: '',
    phone: '',
    address: '',
    propertyType: '',
    experienceLevel: '',
    currentHorses: '',
    facilitiesDescription: '',
    horseInterest: '',
    additionalInfo: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await submitRehomingApplication({
        ...formData,
        currentHorses: parseInt(formData.currentHorses) || 0
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <main>
        <div className="page-header">
          <div className="container">
            <h1 className="display-medium">Application Received</h1>
          </div>
        </div>
        <section className="section">
          <div className="container" style={{ maxWidth: '600px' }}>
            <div className="success-message" style={{ padding: '48px', textAlign: 'center' }}>
              <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: '16px' }} />
              <h2 className="heading-2" style={{ marginBottom: '16px' }}>Thank You</h2>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                We have received your rehoming application. Our team will review your details and be in touch within 7-14 days to discuss next steps.
              </p>
              <Link to="/" className="btn-primary">Return to Home</Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <Link to="/what-we-do">What We Do</Link>
            <ChevronRight size={16} />
            <Link to="/what-we-do/rehoming">Rehoming</Link>
            <ChevronRight size={16} />
            <span>Apply</span>
          </div>
          <h1 className="display-medium">Rehoming Application</h1>
          <p className="body-large">
            Apply to provide a loving home for one of our horses.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '64px' }}>
            {/* Info Panel */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>The Rehoming Process</h2>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                We take great care to match our horses with suitable homes. Our thorough vetting process ensures the best outcome for both horse and carer.
              </p>

              <div 
                style={{ 
                  background: 'white', 
                  padding: '24px', 
                  borderRadius: '16px', 
                  border: '1px solid var(--border-medium)',
                  marginBottom: '24px'
                }}
              >
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>What to Expect</h3>
                <ol style={{ paddingLeft: '20px', marginBottom: '0' }}>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>Submit your application (this form)</li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>Initial assessment and phone conversation</li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>Home/facility visit by our team</li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>Meet and greet with suitable horses</li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>Trial period at your premises</li>
                  <li className="body-medium">Loan agreement signing</li>
                </ol>
              </div>

              <div 
                style={{ 
                  background: 'var(--bg-subtle)', 
                  padding: '20px', 
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}
              >
                <h4 className="heading-3\" style={{ marginBottom: '12px' }}>Important Notes</h4>
                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                  <li className="body-small" style={{ marginBottom: '8px' }}>All horses remain the property of The Grazing Trust</li>
                  <li className="body-small" style={{ marginBottom: '8px' }}>We conduct regular welfare visits</li>
                  <li className="body-small" style={{ marginBottom: '8px' }}>Horses must not be sold, loaned, or bred from</li>
                  <li className="body-small">We guarantee to take horses back if circumstances change</li>
                </ul>
              </div>

              <div 
                style={{ 
                  background: '#FEF3C7', 
                  padding: '16px', 
                  borderRadius: '12px', 
                  display: 'flex', 
                  gap: '12px'
                }}
              >
                <AlertCircle size={20} color="#92400E" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p className="body-small" style={{ color: '#92400E' }}>
                  Submitting this application does not guarantee approval. All applications are assessed individually.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="network-card" style={{ padding: '40px' }}>
              <h2 className="heading-2" style={{ marginBottom: '8px' }}>Application Form</h2>
              <p className="body-small" style={{ marginBottom: '32px' }}>
                Please complete all sections as fully as possible.
              </p>

              {error && (
                <div 
                  style={{ 
                    background: '#FEE2E2', 
                    padding: '16px', 
                    borderRadius: '12px', 
                    marginBottom: '24px',
                    color: '#DC2626'
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>Your Details</h3>
                
                <div className="form-group">
                  <Label htmlFor="applicantName">Full Name *</Label>
                  <Input 
                    id="applicantName" 
                    value={formData.applicantName}
                    onChange={(e) => handleInputChange('applicantName', e.target.value)}
                    required
                    style={{ marginTop: '8px' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email" 
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone" 
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="address">Full Address *</Label>
                  <Textarea 
                    id="address" 
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Include postcode"
                    required
                    style={{ marginTop: '8px', minHeight: '80px' }}
                  />
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-medium)', margin: '32px 0' }} />

                <h3 className="heading-3" style={{ marginBottom: '16px' }}>About Your Facilities</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <Label>Property Type *</Label>
                    <Select 
                      value={formData.propertyType}
                      onValueChange={(value) => handleInputChange('propertyType', value)}
                    >
                      <SelectTrigger style={{ marginTop: '8px' }}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="own_land">Own land/property</SelectItem>
                        <SelectItem value="livery_diy">DIY Livery</SelectItem>
                        <SelectItem value="livery_part">Part Livery</SelectItem>
                        <SelectItem value="livery_full">Full Livery</SelectItem>
                        <SelectItem value="rented">Rented grazing</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <Label htmlFor="currentHorses">Current Number of Horses</Label>
                    <Input 
                      id="currentHorses" 
                      type="number"
                      min="0"
                      value={formData.currentHorses}
                      onChange={(e) => handleInputChange('currentHorses', e.target.value)}
                      placeholder="0"
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Label>Your Experience Level *</Label>
                  <Select 
                    value={formData.experienceLevel}
                    onValueChange={(value) => handleInputChange('experienceLevel', value)}
                  >
                    <SelectTrigger style={{ marginTop: '8px' }}>
                      <SelectValue placeholder="Select your experience level..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner (less than 2 years)</SelectItem>
                      <SelectItem value="intermediate">Intermediate (2-5 years)</SelectItem>
                      <SelectItem value="experienced">Experienced (5-10 years)</SelectItem>
                      <SelectItem value="very_experienced">Very Experienced (10+ years)</SelectItem>
                      <SelectItem value="professional">Professional/Instructor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="facilitiesDescription">Describe Your Facilities *</Label>
                  <Textarea 
                    id="facilitiesDescription" 
                    value={formData.facilitiesDescription}
                    onChange={(e) => handleInputChange('facilitiesDescription', e.target.value)}
                    placeholder="Please describe your facilities including stabling, turnout, fencing, shelter, and any other relevant information..."
                    required
                    style={{ marginTop: '8px', minHeight: '120px' }}
                  />
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-medium)', margin: '32px 0' }} />

                <h3 className="heading-3" style={{ marginBottom: '16px' }}>Additional Information</h3>

                <div className="form-group">
                  <Label htmlFor="horseInterest">Interested in a Specific Horse?</Label>
                  <Input 
                    id="horseInterest" 
                    value={formData.horseInterest}
                    onChange={(e) => handleInputChange('horseInterest', e.target.value)}
                    placeholder="If you've seen a horse on our website, enter their name here"
                    style={{ marginTop: '8px' }}
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="additionalInfo">Anything Else?</Label>
                  <Textarea 
                    id="additionalInfo" 
                    value={formData.additionalInfo}
                    onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                    placeholder="Tell us anything else you think we should know..."
                    style={{ marginTop: '8px', minHeight: '100px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '32px' }}>
                  <Checkbox 
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => handleInputChange('consent', checked)}
                    style={{ marginTop: '3px' }}
                  />
                  <Label htmlFor="consent" style={{ fontWeight: 400, fontSize: '14px', lineHeight: 1.5 }}>
                    I confirm that the information provided is accurate. I understand that submitting this application does not guarantee approval, and that The Grazing Trust will conduct home visits and reference checks as part of the assessment process. *
                  </Label>
                </div>

                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!formData.consent || !formData.propertyType || !formData.experienceLevel || loading}
                  style={{ width: '100%', opacity: (formData.consent && formData.propertyType && formData.experienceLevel && !loading) ? 1 : 0.5 }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} style={{ marginRight: '8px' }} />
                      Submit Application
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default RehomingApplicationPage;
